import React, { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useMemo, useState } from 'react';
import { useConsent } from '../hooks/consent/useConsent';

interface SessionInfo {
  sessionId: string;
  currentQuery: string;
  isLoadingCurrentQuery: boolean;
}

interface SessionContextType {
  isError: boolean;
  isLoading: boolean;
  isConsentLoading: boolean;
  lastCreatedSessionId?: string;
  sessionInfo: SessionInfo;
  acceptedTerms: boolean;
  searchTerm: string;
  setSessionInfo: Dispatch<SetStateAction<SessionInfo>>;
  setAcceptedTerms: () => void;
  createSession: (sessionId: string) => void;
  selectSession: (sessionId: string) => void;
  setSearchTerm: (value: string) => void;
}

const defaultSessionInfo: SessionInfo = {
  sessionId: '',
  currentQuery: '',
  isLoadingCurrentQuery: false,
};

const defaultSessionContext: SessionContextType = {
  isError: false,
  isLoading: true,
  isConsentLoading: true,
  sessionInfo: defaultSessionInfo,
  acceptedTerms: false,
  searchTerm: '',
  setSessionInfo: () => {},
  setAcceptedTerms: () => {},
  createSession: () => {},
  selectSession: () => {},
  setSearchTerm: () => {},
};

export const SessionContext = createContext<SessionContextType>(defaultSessionContext);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sessionInfo, setSessionInfo] = useState<SessionInfo>(defaultSessionInfo);
  const [lastCreatedSessionId, setLastCreatedSessionId] = useState<string>();
  const { consent, accept, isLoading, isError } = useConsent();
  const [searchTerm, setSearchTerm] = useState('');
  const acceptedTerms = !!consent?.consentDate;

  const createSession = useCallback(
    (sessionId: string) => {
      setSessionInfo((prev) => ({ ...prev, sessionId }));
      setLastCreatedSessionId(sessionId);
    },
    [setSessionInfo, setLastCreatedSessionId]
  );

  const selectSession = useCallback(
    (sessionId: string) => {
      setSessionInfo((prev) => ({ ...prev, sessionId }));
    },
    [setSessionInfo]
  );

  const contextValue = useMemo<SessionContextType>(() => {
    return {
      isLoading: isLoading || !sessionInfo.sessionId,
      isConsentLoading: isLoading,
      isError,
      sessionInfo,
      acceptedTerms,
      lastCreatedSessionId,
      searchTerm,
      setSessionInfo,
      setAcceptedTerms: accept,
      createSession,
      selectSession,
      setSearchTerm,
    };
  }, [
    sessionInfo,
    acceptedTerms,
    setSessionInfo,
    accept,
    isLoading,
    isError,
    lastCreatedSessionId,
    createSession,
    selectSession,
    searchTerm,
    setSearchTerm,
  ]);

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};
